/* eslint-disable react/prop-types */
import React, { useEffect, useMemo, useState } from 'react';
import { DeleteOutlined, SearchOutlined, QuestionCircleOutlined } from '@ant-design/icons';
import {
  Button,
  Card,
  Col,
  DatePicker,
  Form,
  Grid,
  Input,
  Row,
  Segmented,
  Space,
  Spin,
  Typography,
  Tooltip,
} from 'antd';
import { getCiudadano } from 'api/cuentaunicasir';
import { getContribuyente } from 'api/empresas/contribuyente';
import { getDiasNoLaborales, getEstadosCitas, getVentanillas } from 'api/tramites';
import { getCitas, patchCita } from 'api/tramites/citas';
import Select from 'components/Select';
import Table from 'components/Table';
import { useSelector } from 'react-redux';
import { getDistinctValues, listToString, toMoment } from 'utils/normalizers';
import padronToString, { getNombreCompleto } from 'utils/padronesToString';
import ConsultaCita, { estadosCitasMap, fillValuesCita } from 'views/Tramites/GPM/components/ConsultaCita';
import FormSubmitControls from 'components/FormSubmitControls';
import { getPlantillaIndexada, getPlantillasIndexadas } from 'api/tramites/plantillasDeTramites';
import { getGrupos } from 'api/catalogos/grupos';
import { getUsuarios } from 'api/usuarios/usuarios';
import { getHistorialDeCitas } from 'api/tramites/historialDeCita';
import { useHistory } from 'react-router-dom';
import { getRazonesDeRechazoCita } from 'api/recaudacion';
import AppointmentsCalendar, { TagCita, colorsByStatusMap } from './Calendar';

const today = toMoment(new Date());
const DEV = process.env.NODE_ENV === 'development';

export const permissionConfig = {
  mainPermission: 'viewcrud_cita',
  required: [
    'view_cita',
    'view_ciudadano',
    'view_contribuyente',
    'view_plantilladetramite',
    'view_ventanilla',
    'view_group',
    'view_usuario',
    'view_historialdecita',
    'view_razonderechazoparacita',
    'view_estatusdecitas',
    'view_dianolaboral',
  ],
  customActions: [
    {
      name: 'change_cita',
      title: 'Cambiar cita',
      permissions: ['change_cita'],
    },
    {
      name: 'asignarse_cita',
      title: 'Asignarse cita',
      permissions: ['asignarse_cita', 'change_cita'],
    },
    {
      name: 'asignar_cita_a_usuario',
      title: 'Asignar cita a usuario',
      permissions: ['asignar_cita_a_usuario', 'change_cita'],
    },
  ],
};

const initialState = [{ id: 0, nombre: 'Todos', descripcion: 'Todos' }];
const REPUVE = process.env.REACT_APP_ENV_NAME === 'repuve';

const SeguimientoDeCitas = ({ permission }) => {
  const history = useHistory();
  const user = useSelector(({ auth }) => auth.user);
  const userRoles = useSelector(({ auth }) => auth.roles);
  const screens = Grid.useBreakpoint();

  const [loading, setLoading] = useState(true);
  const [visible, setVisible] = useState(false);
  const [plantillas, setPlantillas] = useState(initialState);
  const [estados, setEstados] = useState(initialState);
  const [roles, setRoles] = useState([]);
  const [usuarios, setUsuarios] = useState([]);
  const [historial, setHistorial] = useState([]);
  const [data, setData] = useState({ results: [] });
  const [citas, setCitas] = useState([]);
  const [formFilterTable] = Form.useForm();
  const [formFilter] = Form.useForm();
  const [formDetalles] = Form.useForm();
  const [formCambioEstado] = Form.useForm();
  const [selectedRowKeys, setSelectedRowKeys] = useState([]);
  const [mode, setMode] = useState('table');
  const [usuariosAsignar, setUsuariosAsignar] = useState([]);
  const [diasNoLaborales, setDiasNoLaborales] = useState([]);
  const [oficina, setOficina] = useState();
  const [razones, setRazones] = useState([]);

  const estado_de_cita = Form.useWatch('estado_de_cita', formCambioEstado);
  const fecha_cita_before = Form.useWatch('fecha_cita_before', formFilterTable);
  const fecha_cita_after = Form.useWatch('fecha_cita_after', formFilterTable);
  const _oficina = Form.useWatch('oficina', formFilter);
  // const calendarMode = Form.useWatch('calendarMode', formFilter);
  const [cita, setCita] = useState();

  const userPortal = usuarios.find((u) => u.canal_de_pago === 4 && u.estados_globales === 4);

  const citaTemp = (mode === 'table' ? data.results : citas).find((c) => c.id === selectedRowKeys[0]);
  const plantilla = plantillas.find((p) => p.id === (cita || citaTemp)?.plantilla_de_tramite);

  const oficinas = useMemo(() => {
    const _oficinas = plantillas.map((p) => p.oficina_de_atencion || []).flat();
    const ids = getDistinctValues(_oficinas.map((o) => o.id));
    return ids.map((id) => _oficinas.find((o) => o.id === id));
  }, [plantillas]);

  const fetchData = async (page = 1, _plantillas = plantillas, clear = true) => {
    if (!_plantillas.length) return;
    setLoading(true);
    const filters = formFilterTable.getFieldsValue();
    const params = {
      page,
      ...filters,
      usuario_puede_atender: !permission.asignar_cita_a_usuario && permission.asignarse_cita,
      plantilla_de_tramite: filters.plantilla_de_tramite || undefined,
      estado_de_cita: filters.estado_de_cita || undefined,
      fecha_cita_before: filters.fecha_cita_before?.format('YYYY-MM-DD'),
      fecha_cita_after: filters.fecha_cita_after?.format('YYYY-MM-DD'),
    };
    const _data = await getCitas(params);
    _data.page = page;
    const _ciudadanos = getDistinctValues(_data.results.map((e) => e.ciudadano).filter((e) => e));
    const _contribuyentes = getDistinctValues(_data.results
      .map((e) => e.contribuyente).filter((e) => e));
    const ciudadanos = await Promise.all(_ciudadanos.map((c) => getCiudadano(c)));
    const contribuyentes = await Promise.all(_contribuyentes.map((c) => getContribuyente(c)));
    _data.results = _data.results.map(({ ciudadano, contribuyente, ...c }) => ({
      ...c,
      ciudadano: ciudadano ? ciudadanos.find((e) => e.id === ciudadano) : null,
      contribuyente: contribuyente ? contribuyentes.find((e) => e.id === contribuyente) : null,
    }));
    setData(_data);
    setLoading(false);
    if (clear) {
      setSelectedRowKeys([]);
    }
  };

  const fetchCitas = async () => {
    const { fecha, calendarMode, ..._params } = formFilter.getFieldsValue();
    if (!_params.oficina) return;
    const params = { ..._params, page: 1 };
    if (!permission.asignar_cita_a_usuario && permission.asignarse_cita) {
      params.usuario_puede_atender = true;
    }
    if (calendarMode === 'day') {
      params.fecha_cita_after = fecha.clone();
      params.fecha_cita_before = fecha.clone();
    }
    if (calendarMode === 'week') {
      params.fecha_cita_after = fecha.clone().startOf('week');
      params.fecha_cita_before = fecha.clone().endOf('week');
    }
    if (calendarMode === 'month' && !params.fecha_cita_after) {
      params.fecha_cita_after = fecha.clone().startOf('month');
      params.fecha_cita_before = fecha.clone().endOf('month');
    }

    params.fecha_cita_after = params.fecha_cita_after?.format('YYYY-MM-DD');
    params.fecha_cita_before = params.fecha_cita_before?.format('YYYY-MM-DD');

    if (JSON.stringify(citas.params || '{}') === JSON.stringify(params)) return;

    setLoading(true);
    let _data = await getCitas(params, true);
    const _ciudadanos = getDistinctValues(_data.map((e) => e.ciudadano).filter((e) => e));
    const _contribuyentes = getDistinctValues(_data.map((e) => e.contribuyente).filter((e) => e));
    const ciudadanos = await Promise.all(_ciudadanos.map((c) => getCiudadano(c)));
    const contribuyentes = await Promise.all(_contribuyentes.map((c) => getContribuyente(c)));
    _data = _data.map(({ ciudadano: _ciudadano, contribuyente: _contribuyente, ...c }) => {
      const ciudadano = _ciudadano ? ciudadanos.find((e) => e.id === _ciudadano) : null;
      const contribuyente = _contribuyente
        ? contribuyentes.find((e) => e.id === _contribuyente) : null;
      const nombre_solicitante = getNombreCompleto({ ciudadano, contribuyente });
      return {
        ...c,
        nombre_solicitante,
        ciudadano,
        contribuyente,
        plantilla_de_tramite__nombre: plantillas
          .find((p) => p.id === c.plantilla_de_tramite)?.nombre,
      };
    });
    _data.params = params;
    setCitas(_data);
    setOficina(oficinas.find((o) => o.id === _oficina));
    setLoading(false);
  };

  const getPlantillas = async () => {
    if (permission.asignar_cita_a_usuario) {
      const _plantillas = await getPlantillasIndexadas({ requiere_cita: true });
      setPlantillas([
        ...initialState,
        ..._plantillas,
      ]);
      await fetchData(1, _plantillas);
      return;
    }
    const ventanillas = (await Promise.all([
      getVentanillas({ usuario: user.id }),
      ...userRoles.map((r) => getVentanillas({ groups: r.id })),
    ])).flat();
    const ids = getDistinctValues(ventanillas.map((v) => v.plantillas_de_tramites).flat());
    const _plantillas = await Promise.all(ids.map((e) => getPlantillaIndexada(e)));
    setPlantillas([
      ...initialState,
      ..._plantillas,
    ]);
    await fetchData(1, _plantillas);
  };

  useEffect(() => {
    const fetch = async () => {
      const [
        _estados,
        _roles,
        _usuarios,
        _diasNoLaborales,
        _razones,
      ] = await Promise.all([
        getEstadosCitas(),
        getGrupos(),
        getUsuarios(),
        getDiasNoLaborales(),
        getRazonesDeRechazoCita({ es_razon_rechazo_ciudadano: false }),
        getPlantillas(),
      ]);
      setEstados([...initialState, ..._estados]);
      setRoles(_roles);
      setUsuarios(_usuarios);
      setDiasNoLaborales(_diasNoLaborales);
      setRazones(_razones);
      setLoading(false);
    };
    fetch();
  }, []);

  const userToString = (_user) => {
    if (!_user) return '';
    return `${_user.numero_de_empleado || ''} ${_user.nombre_completo || ''}`.trim();
  };

  const getUserForCol = (u) => {
    if (typeof u === 'string') return u;
    if (typeof u === 'object') return userToString(u);
    if (typeof u === 'number') return userToString(usuarios.find((_u) => _u.id === u));
    return null;
  };

  const columns = [
    {
      titleText: 'Folio',
      width: 150,
      dataIndex: 'folio',
      key: 'folio',
      filterDropdown: ({ setSelectedKeys, selectedKeys, confirm }) => (
        <div style={{ padding: 8 }}>
          <Input
            placeholder="Buscar folio"
            value={selectedKeys[0]}
            onChange={(e) => setSelectedKeys(e.target.value ? [e.target.value] : [])}
            onPressEnter={confirm}
          />
        </div>
      ),
      onFilter: (value, record) => record.folio
        ?.toString().toLowerCase().includes(value.toLowerCase()),
    },
    {
      titleText: 'Estado',
      width: 150,
      dataIndex: 'estado_de_cita',
      key: 'estado_de_cita',
      render: (_estado_de_cita) => (
        <TagCita style={{ backgroundColor: colorsByStatusMap[_estado_de_cita] }}>
          {estados.find((e) => e.id === _estado_de_cita)?.descripcion}
        </TagCita>
      ),
    },
    {
      titleText: 'Solicitante',
      width: 350,
      dataIndex: 'solicitante',
      key: 'solicitante',
      render: (_, record) => padronToString(
        record.contribuyente || record.ciudadano,
        record.contribuyente ? 15 : 1,
      ),
      filterDropdown: ({ setSelectedKeys, selectedKeys, confirm }) => (
        <div style={{ padding: 8 }}>
          <Input
            placeholder="Buscar solicitante"
            value={selectedKeys[0]}
            onChange={(e) => setSelectedKeys(e.target.value ? [e.target.value] : [])}
            onPressEnter={confirm}
            allowClear
          />
        </div>
      ),
      onFilter: (value, record) => {
        const usuario = record.contribuyente || record.ciudadano;
        const valorBusqueda = value.toLowerCase();
        return usuario
          && (
            (usuario.nombre && usuario.nombre_completo.toLowerCase().includes(valorBusqueda))
            || (usuario.clave && usuario.clave.toLowerCase().includes(valorBusqueda))
          );
      },

    },
    {
      titleText: 'Trámite',
      width: 350,
      dataIndex: 'plantilla_de_tramite',
      key: 'plantilla_de_tramite',
      render: (_plantilla) => plantillas.find((p) => p.id === _plantilla)?.nombre,
    },
    {
      titleText: 'Fecha',
      width: 100,
      dataIndex: 'fecha_cita',
      key: 'fecha_cita',
      render: (fecha_cita) => fecha_cita?.format?.(),
    },
    {
      titleText: 'Horario',
      width: 150,
      dataIndex: 'horario',
      key: 'horario',
      render: (_, r) => `${r.horario_inicio.format('hh:mm a')} - ${r.horario_fin.format('hh:mm a')}`,
    },
    {
      titleText: 'Último usuario modificó',
      width: 250,
      dataIndex: 'ultimo_usuario_que_modifico',
      key: 'ultimo_usuario_que_modifico',
      render: (ultimo_usuario_que_modifico, record) => {
        if (ultimo_usuario_que_modifico) return getUserForCol(ultimo_usuario_que_modifico);
        return getUserForCol(record.usuario_creo || userPortal);
      },
    },
    {
      titleText: `${REPUVE ? 'Perito' : 'Usuario'} que atendió`,
      width: 250,
      dataIndex: 'usuario_que_atendio',
      key: 'usuario_que_atendio',
      render: (usuario_que_atendio) => {
        if (usuario_que_atendio) return getUserForCol(usuario_que_atendio);
        return '';
      },
      filterDropdown: ({ setSelectedKeys, selectedKeys, confirm }) => (
        <div style={{ padding: 8 }}>
          <Input
            placeholder={`Buscar ${REPUVE ? 'perito' : 'usuario'}`}
            value={selectedKeys[0]}
            onChange={(e) => setSelectedKeys(e.target.value ? [e.target.value] : [])}
            onPressEnter={confirm}
          />
        </div>
      ),
      onFilter: (value, record) => getUserForCol(record.usuario_que_atendio)
        .toLowerCase()
        .includes(value.toLowerCase()),
    },
  ].filter((e) => e);

  const columnsHistorial = [
    {
      titleText: 'Estado',
      width: 150,
      dataIndex: 'estado_de_cita',
      key: 'estado_de_cita',
      render: (estado) => estados.find((e) => e.id === estado)?.descripcion,
    },
    {
      titleText: 'Observaciones',
      width: 350,
      dataIndex: 'observaciones',
      key: 'observaciones',
    },
    {
      titleText: 'Usuario',
      width: 250,
      dataIndex: 'usuario_creo',
      key: 'usuario_creo',
      render: (u) => getUserForCol(u || userPortal),
    },
    {
      titleText: 'Fecha',
      width: 120,
      dataIndex: 'fecha_de_creacion',
      key: 'fecha_de_creacion',
      render: (f) => toMoment(f)?.format(),
    },
  ];

  const rules = { required: [{ required: true }] };

  const rowSelection = {
    selectedRowKeys,
    type: 'radio',
  };

  const handleOnRowClick = (record) => setSelectedRowKeys([record.id]);

  const userToOption = (_user, v) => ({
    value: `${v.id}__${_user.id}`,
    label: DEV ? userToString(_user).concat(` (${_user.id})`) : userToString(_user),
    idUsuario: _user.id,
    disabled: !!_user.disabled,
  });

  const ventanillasToOptions = (ventanillas, users) => ventanillas
    .filter((v) => v.plantillas_de_tramites.includes(plantilla.id))
    .map((v) => {
      if (!v.usuario && !v.groups.length) return null;
      if (v.groups.length) {
        const _roles = roles.filter((r) => v.groups.includes(r.id)).map((r) => r.name);
        const _users = users
          .filter((u) => u.rol.some((r) => _roles.includes(r)))
          .map((u) => userToOption(u, v));
        const rolRoles = _roles.length ? 'Roles' : 'Rol';
        return {
          value: `ventanilla_${v.id}`,
          label: (
            <>
              <b>{v.descripcion}</b>
              <br />
              {`${rolRoles}: ${listToString(_roles)}`}
            </>
          ),
          disabled: true,
          roles: _roles,
          options: _users.length ? _users
            : [userToOption({ disabled: true, nombre_completo: `${rolRoles} sin usuarios` }, v)],
        };
      }
      const _user = users.find((u) => u.id === v.usuario);
      return {
        value: `ventanilla_${v.id}`,
        label: v.descripcion,
        disabled: true,
        options: [userToOption(_user, v)],
      };
    }).filter((e) => e);

  const onClickExpand = async (__cita) => {
    setLoading(true);
    const _cita = __cita
      || (mode === 'table' ? data.results : citas).find((c) => c.id === selectedRowKeys[0]);
    setLoading(true);
    formDetalles.setFieldsValue({
      solicitante: padronToString(_cita.contribuyente
        || _cita.ciudadano, _cita.contribuyente ? 15 : 1),
    });
    await fillValuesCita(
      _cita,
      formDetalles,
      plantillas.find((p) => p.id === _cita.plantilla_de_tramite),
    );
    const _historial = await getHistorialDeCitas({ cita: selectedRowKeys[0] });
    if (_cita.estado_de_cita === estadosCitasMap.PROGRAMADA) {
      const oficinaSelected = plantilla?.oficina_de_atencion.find((o) => o.id === _cita?.oficina);
      if (permission.asignar_cita_a_usuario) {
        const citasEnProceso = await getCitas({ estado_de_cita: estadosCitasMap.EN_PROCESO }, true);
        // const _usuarios = usuarios.map((u) => ({
        //   ...u,
        //   disabled: citasEnProceso.some((c) => c.usuario_que_atendio === u.id),
        // }));
        const _usuarios = usuarios.filter((u) => !citasEnProceso
          .some((c) => c.usuario_que_atendio === u.id));
        const _ventanillasConUsuarios = ventanillasToOptions(oficinaSelected.ventanilla, _usuarios);
        setUsuariosAsignar(_ventanillasConUsuarios);
      } else if (permission.asignarse_cita) {
        const citasEnProcesoUsuario = await getCitas({
          estado_de_cita: estadosCitasMap.EN_PROCESO,
          usuario_que_atendio: user.id,
        });
        if (!citasEnProcesoUsuario.length) {
          const _ventanillasUsuario = ventanillasToOptions(
            oficinaSelected.ventanilla,
            [usuarios.find((u) => u.id === user.id)],
          );
          setUsuariosAsignar(_ventanillasUsuario);
          formCambioEstado.setFieldsValue({
            estado_de_cita: estadosCitasMap.EN_PROCESO,
            ventanilla__usuario_que_atendio: _ventanillasUsuario.length === 1 ? _ventanillasUsuario
              .options.find((o) => o.idUsuario === user.id).id : undefined,
          });
        }
      }
    }
    setHistorial(_historial);
    setCita(_cita);
    setVisible(true);
    setLoading(false);
  };

  const onCancel = () => {
    setCita();
    setVisible(false);
    setUsuariosAsignar([]);
    setHistorial([]);
    setSelectedRowKeys([]);
    formCambioEstado.resetFields();
    formDetalles.resetFields();
  };

  const onFinish = async () => {
    setLoading(true);
    const values = formCambioEstado.getFieldsValue();
    if (values.ventanilla__usuario_que_atendio) {
      [values.ventanilla, values.usuario_que_atendio] = values.ventanilla__usuario_que_atendio
        .split('__').map((val) => parseInt(val, 10));
    }
    delete values.ventanilla__usuario_que_atendio;
    if (values.estado_de_cita === estadosCitasMap.FINALIZADA) {
      values.estados_globales = 4; // autorizado
    } else if (values.estado_de_cita === estadosCitasMap.CANCELADA) {
      values.estados_globales = 5; // cancelado
    }
    const updated = await patchCita(selectedRowKeys[0], values);
    if (!updated) {
      setLoading(false);
      return;
    }
    await fetchData(data.page, plantillas, false);
    setTimeout(async () => {
      await onClickExpand(updated);
      setLoading(false);
      formCambioEstado.resetFields();
    });
  };

  const disabledMap = {
    [estadosCitasMap.EN_PROCESO]: cita?.estado_de_cita !== estadosCitasMap.PROGRAMADA
      || !usuariosAsignar.length,
    [estadosCitasMap.NO_ASISTIO]: cita?.estado_de_cita !== estadosCitasMap.PROGRAMADA,
    [estadosCitasMap.FINALIZADA]: cita?.estado_de_cita !== estadosCitasMap.EN_PROCESO,
    [estadosCitasMap.CANCELADA]: ![estadosCitasMap.PROGRAMADA, estadosCitasMap.EN_PROCESO]
      .includes(cita?.estado_de_cita),
  };

  const estadosAsignables = estados.filter((e) => e.id > 1)
    .map((e) => ({ ...e, disabled: disabledMap[e.id] }));

  return (
    <Spin tip="Cargando" spinning={loading}>
      {!visible && DEV && (
        <Segmented
          size="large"
          value={mode}
          onChange={setMode}
          options={[
            {
              label: 'Mi calendario de citas',
              value: 'table',
            },
            {
              label: 'Calendario',
              value: 'calendar',
            },
          ]}
          style={{ marginBottom: 10 }}
        />
      )}
      <Form
        onFinish={() => fetchData()}
        form={formFilterTable}
        layout="vertical"
        initialValues={{
          plantilla_de_tramite: 0,
          estado_de_cita: !permission.asignar_cita_a_usuario ? 0 : estadosCitasMap.Todos,
          // fecha_cita_before: !permission.asignar_cita_a_usuario ? undefined : today,
          // fecha_cita_after: !permission.asignar_cita_a_usuario ? undefined : today,
        }}
        style={{ display: mode === 'table' && !visible ? undefined : 'none' }}
      >
        <Row gutter={10}>
          <Col xs={24} sm={24} md={8}>
            <Form.Item label="Trámite" name="plantilla_de_tramite">
              <Select dataSource={plantillas} labelProp="nombre" allowClear={false} />
            </Form.Item>
          </Col>
          <Col xs={24} sm={24} md={8}>
            <Form.Item label="Desde" name="fecha_cita_after">
              <DatePicker
                format="DD/MM/YYYY"
                disabledDate={fecha_cita_before ? (d) => d.isAfter(fecha_cita_before) : null}
              />
            </Form.Item>
          </Col>
          <Col xs={24} sm={24} md={8}>
            <Form.Item label="Hasta" name="fecha_cita_before">
              <DatePicker
                format="DD/MM/YYYY"
                disabledDate={fecha_cita_after ? (d) => d.isBefore(fecha_cita_after) : null}
              />
            </Form.Item>
          </Col>
          <Col xs={24} sm={24} md={8}>
            <Form.Item label="Estado" name="estado_de_cita">
              <Select dataSource={estados} allowClear={false} />
            </Form.Item>
          </Col>
          <Col xs={24} sm={24} md={8}>
            <Form.Item style={{ marginTop: screens.md ? 22 : 0 }}>
              <Space>
                <Button htmlType="submit">
                  <SearchOutlined />
                </Button>
                <Button
                  onClick={() => setTimeout(() => formFilterTable.submit())}
                  htmlType="reset"
                >
                  <DeleteOutlined />
                </Button>
              </Space>
            </Form.Item>
          </Col>
        </Row>
      </Form>
      {mode === 'table' && !visible && (
        <Table
          data={data.results}
          cols={columns}
          allowSort={false}
          allowSearch={false}
          pagination={false}
          allowColumnSelector={false}
          paginationCallback={(page) => fetchData(page)}
          expandable={false}
          count={data.count}
          currentPage={data.page}
          restHeigth={200}
          rowKey="id"
          handleOnRowClick={handleOnRowClick}
          controls={{ onClickExpand }}
          permission={{ view: true, hasAllViewPermission: true }}
          rowSelection={rowSelection}
          allowExpand
          emptyText={!permission.asignar_cita_a_usuario && !plantillas.length && !loading
            ? 'Usuario no atiende ninguna ventanilla' : null}
        />
      )}
      {mode === 'calendar' && !visible && (
        <Form
          onFinish={() => fetchCitas()}
          form={formFilter}
          layout="vertical"
          initialValues={{ calendarMode: 'day', fecha: today }}
        >
          <Row gutter={10}>
            {/* {calendarMode !== 'month' && (
              <> */}
            <Col xs={24} sm={24} md={8} order={2}>
              <Form.Item label="Oficina" name="oficina" rules={rules.required}>
                <Select dataSource={oficinas} />
              </Form.Item>
            </Col>
            <Col xs={24} sm={24} md={8} order={3}>
              <Form.Item label="Ventanilla" name="ventanilla">
                <Select
                  dataSource={oficinas.find((o) => o.id === _oficina)?.ventanilla || []}
                />
              </Form.Item>
            </Col>
            <Col xs={24} sm={24} md={8} order={4}>
              <Form.Item style={{ marginTop: screens.md ? 22 : 0 }}>
                <Space>
                  <Button htmlType="submit">
                    <SearchOutlined />
                  </Button>
                  <Button
                    onClick={() => setTimeout(() => formFilter.submit())}
                    htmlType="reset"
                  >
                    <DeleteOutlined />
                  </Button>
                </Space>
              </Form.Item>
            </Col>
            {/* </>
            )} */}
            <Form.Item name="calendarMode" hidden>
              <Input />
            </Form.Item>
          </Row>
          <Row gutter={10}>
            <AppointmentsCalendar
              diasNoLaborales={diasNoLaborales}
              citas={citas}
              fetchCitas={fetchCitas}
              oficina={oficina}
              onClickExpand={onClickExpand}
            />
          </Row>
        </Form>
      )}
      {visible && (
        <Card
          title="Detalles de la cita"
          extra={(<FormSubmitControls onCancel={onCancel} loading={loading} />)}
          bordered={false}
          className="form-container"
        >
          <Form form={formDetalles} style={{ marginTop: 10 }}>
            <Row gutter={[10, 10]}>
              <ConsultaCita autoFill={false} />
              <Col xs={24} sm={24} md={12} order={5}>
                <Form.Item label="Folio trámite" name="folio_tramite">
                  <Input
                    disabled
                    addonAfter={(cita?.tramite || (plantilla?.generar_tramite_posterior_a_cita
                      && cita?.estado_de_cita === estadosCitasMap.EN_PROCESO)) && (
                      <Button
                        type="link"
                        onClick={() => history.push(`/tramites/registrar-tramite/?${cita.tramite
                          ? `tramite=${cita.tramite}` : `cita=${cita.id}`}`)}
                        style={{ padding: 0, height: 'auto' }}
                      >
                        <Tooltip title={!cita.tramite ? 'Ir a generación del trámite' : 'Ir a seguimiento del trámite'}>
                          Seguimiento
                          {' '}
                          <QuestionCircleOutlined />
                        </Tooltip>
                      </Button>
                    )}
                  />
                </Form.Item>
              </Col>
              <Col xs={24} sm={24} md={12} order={6}>
                <Form.Item label="Oficina" name="oficina">
                  <Input disabled />
                </Form.Item>
              </Col>
              <Col span={24} order={7}>
                <Form.Item label="Solicitante" name="solicitante">
                  <Input disabled />
                </Form.Item>
              </Col>
            </Row>
          </Form>
          <br />
          {[estadosCitasMap.PROGRAMADA, estadosCitasMap.EN_PROCESO]
            .includes(cita?.estado_de_cita) && permission.change_cita && (
            <Form
              form={formCambioEstado}
              labelCol={{ span: 8 }}
              onFinish={onFinish}
            >
              <Col span={24}>
                <Typography.Title level={3} className="primary-text">
                  Cambio de estado
                </Typography.Title>
              </Col>
              <Col xs={24} sm={24} md={20}>
                <Form.Item label="Estado de la cita" name="estado_de_cita" rules={rules.required}>
                  <Select dataSource={estadosAsignables} />
                </Form.Item>
              </Col>
              {estado_de_cita === estadosCitasMap.EN_PROCESO && (
                <Col xs={24} sm={24} md={20}>
                  <Form.Item label="Asignar a usuario" name="ventanilla__usuario_que_atendio" rules={rules.required}>
                    <Select dataSource={usuariosAsignar} labelProp="label" valueProp="value" />
                  </Form.Item>
                </Col>
              )}
              {estado_de_cita === estadosCitasMap.CANCELADA && (
                <Col xs={24} sm={24} md={20}>
                  <Form.Item label="Motivo de cancelación" name="razon_de_rechazo_cita" rules={rules.required}>
                    <Select dataSource={razones} />
                  </Form.Item>
                </Col>
              )}
              <Col xs={24} sm={24} md={20}>
                <Form.Item label="Observaciones" name="observaciones">
                  <Input.TextArea autoSize={{ minRows: 2, maxRows: 2 }} />
                </Form.Item>
              </Col>
              <Col xs={24} sm={24} md={20}>
                <Form.Item style={{ float: 'right' }}>
                  <Button htmlType="submit">
                    Guardar
                  </Button>
                </Form.Item>
              </Col>
            </Form>
          )}
          <Col span={24} style={{ marginTop: 25 }}>
            <Typography.Title level={3} className="primary-text">
              Historial de la cita
            </Typography.Title>
          </Col>
          <Table
            data={historial}
            cols={columnsHistorial}
            allowSort={false}
            allowSearch={false}
            allowColumnSelector={false}
            expandable={false}
            restHeigth={screens.md ? 220 : 150}
            rowKey="id"
            hasAllViewPermission
          />
        </Card>
      )}
    </Spin>
  );
};

export default SeguimientoDeCitas;
